import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Mic, MicOff, X } from "lucide-react";
import { usePitchDetector } from "../hooks/usePitchDetector";
import { HOME_THEME as T } from "../lib/homeTheme";

const STRING_LABELS = [
  { num: 6, label: "6ª grave", note: "E2", freq: 82.41 },
  { num: 5, label: "5ª", note: "A2", freq: 110.0 },
  { num: 4, label: "4ª", note: "D3", freq: 146.83 },
  { num: 3, label: "3ª", note: "G3", freq: 196.0 },
  { num: 2, label: "2ª", note: "B3", freq: 246.94 },
  { num: 1, label: "1ª aguda", note: "E4", freq: 329.63 },
] as const;

function getClosestString(frequency: number): (typeof STRING_LABELS)[number] | null {
  if (frequency < 60 || frequency > 400) return null;
  let closest = STRING_LABELS[0];
  let minDiff = Math.abs(frequency - closest.freq);
  for (const s of STRING_LABELS) {
    const diff = Math.abs(frequency - s.freq);
    if (diff < minDiff) {
      minDiff = diff;
      closest = s;
    }
  }
  return closest;
}

function getTuningAdvice(
  detectedFreq: number,
  targetFreq: number,
): {
  cents: number;
  action: "tighten" | "loosen" | "perfect";
} {
  const cents = 1200 * Math.log2(detectedFreq / targetFreq);
  if (Math.abs(cents) < 5) return { cents, action: "perfect" };
  return { cents, action: cents > 0 ? "tighten" : "loosen" };
}

function actionColor(action: "tighten" | "loosen" | "perfect" | null | undefined): string {
  if (action === "perfect") return "#86efac";
  if (action === "tighten") return "#fbbf24";
  if (action === "loosen") return T.mint;
  return T.muted;
}

export const TunerOverlay: React.FC<{ expanded: boolean; onClose?: () => void }> = ({
  expanded,
  onClose,
}) => {
  const { state, startListening, stopListening } = usePitchDetector();
  const isListening = state.isListening;

  const detectedString = useMemo(() => {
    if (!state.note || state.frequency < 60) return null;
    return getClosestString(state.frequency);
  }, [state.note, state.frequency]);

  const advice = useMemo(() => {
    if (!detectedString || !state.frequency) return null;
    return getTuningAdvice(state.frequency, detectedString.freq);
  }, [detectedString, state.frequency]);

  useEffect(() => {
    if (!expanded && isListening) stopListening();
  }, [expanded, isListening, stopListening]);

  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [expanded]);

  useEffect(() => {
    if (!expanded || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, onClose]);

  if (!expanded || typeof document === "undefined") return null;

  const statusColor = actionColor(advice?.action);

  const modal = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 sm:p-6 md:p-8"
      style={{ zIndex: 9999 }}
      role="presentation"
    >
      {/* Difuminado de fondo — encima de toda la app */}
      <button
        type="button"
        className="absolute inset-0 border-0 cursor-default"
        aria-label="Cerrar afinador"
        onClick={onClose}
        style={{
          background: "rgba(8, 7, 6, 0.72)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      />

      <div
        className="relative w-full max-w-5xl max-h-[min(92vh,880px)] overflow-y-auto rounded-3xl border"
        style={{
          background: "linear-gradient(165deg, rgba(32,30,28,0.98) 0%, rgba(16,15,14,0.99) 100%)",
          borderColor: "rgba(255,255,255,0.12)",
          boxShadow: `${T.glowMintExpansive}, 0 32px 100px rgba(0,0,0,0.65)`,
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Afinador"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="pointer-events-none absolute -top-28 right-0 h-64 w-96 rounded-full opacity-50 blur-3xl"
          style={{ background: T.glowMint }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-24 left-10 h-52 w-72 rounded-full opacity-35 blur-3xl"
          style={{ background: T.glowLavender }}
          aria-hidden
        />

        {/* Header */}
        <div
          className="relative flex flex-wrap items-center justify-between gap-4 px-5 sm:px-8 py-5 border-b"
          style={{ borderColor: "rgba(255,255,255,0.08)" }}
        >
          <div className="min-w-0">
            <p
              className="text-[11px] font-sans tracking-[0.18em] uppercase"
              style={{ color: T.mint }}
            >
              Bordona
            </p>
            <h2
              className="mt-1 text-2xl sm:text-3xl font-serif font-light tracking-tight"
              style={{ color: T.text }}
            >
              Afinador
            </h2>
            <p className="mt-1 text-sm font-sans" style={{ color: T.muted }}>
              Afinación estándar · E · A · D · G · B · E
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              type="button"
              onClick={() => (isListening ? stopListening() : startListening())}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl text-sm font-sans font-semibold transition-colors"
              style={
                isListening
                  ? {
                      background: "rgba(134,239,172,0.18)",
                      color: "#86efac",
                      border: "1px solid rgba(134,239,172,0.45)",
                    }
                  : {
                      background: "linear-gradient(135deg, #99f6e4, #7ecfc0)",
                      color: "#100f0e",
                      border: "1px solid transparent",
                    }
              }
            >
              {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              {isListening ? "Detener" : "Escuchar"}
            </button>

            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="w-11 h-11 rounded-xl flex items-center justify-center border transition-colors hover:bg-white/10"
                style={{ borderColor: "rgba(255,255,255,0.14)", color: T.muted }}
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="relative px-5 sm:px-8 py-6 sm:py-8 flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Cuerdas */}
          <div className="flex-1 min-w-0">
            <p
              className="mb-3 text-[11px] font-sans tracking-[0.14em] uppercase"
              style={{ color: T.dim }}
            >
              Cuerdas al aire
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {STRING_LABELS.map((s) => {
                const isDetected = detectedString?.num === s.num;
                const isInTune = isDetected && advice?.action === "perfect";
                const border = isInTune
                  ? "rgba(134,239,172,0.55)"
                  : isDetected
                    ? "rgba(251,191,36,0.45)"
                    : "rgba(255,255,255,0.1)";
                const bg = isInTune
                  ? "rgba(134,239,172,0.12)"
                  : isDetected
                    ? "rgba(251,191,36,0.1)"
                    : "rgba(255,255,255,0.04)";
                return (
                  <div
                    key={s.num}
                    className="flex flex-col items-center gap-1 p-4 sm:p-5 rounded-2xl border transition-all min-h-[7.5rem]"
                    style={{ borderColor: border, background: bg }}
                  >
                    <span
                      className="text-[10px] font-sans tracking-wider uppercase"
                      style={{ color: T.dim }}
                    >
                      {s.label}
                    </span>
                    <span
                      className="text-2xl sm:text-3xl font-serif font-light leading-none"
                      style={{ color: isInTune ? "#86efac" : T.text }}
                    >
                      {s.note.replace(/\d/, "")}
                      <span className="text-xs align-super ml-0.5 opacity-55">
                        {s.note.match(/\d/)?.[0]}
                      </span>
                    </span>
                    <span className="text-[11px] font-sans tabular-nums" style={{ color: T.dim }}>
                      {s.freq.toFixed(0)} Hz
                    </span>
                    {isDetected && advice && (
                      <span
                        className="text-xs font-sans font-semibold mt-1"
                        style={{ color: actionColor(advice.action) }}
                      >
                        {advice.action === "perfect"
                          ? "✓ OK"
                          : advice.action === "tighten"
                            ? "↑ Tensa"
                            : "↓ Afloja"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Lectura grande */}
          <div
            className="w-full lg:w-64 xl:w-72 shrink-0 flex flex-col items-center justify-center p-6 sm:p-8 rounded-2xl border min-h-[14rem]"
            style={{
              borderColor: "rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.04)",
            }}
          >
            {state.note ? (
              <>
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-7xl sm:text-8xl font-serif font-light leading-none"
                    style={{ color: statusColor }}
                  >
                    {state.note}
                  </span>
                  <span className="text-xl font-sans" style={{ color: T.muted }}>
                    {state.octave}
                  </span>
                </div>
                <p className="mt-4 text-base font-sans font-medium" style={{ color: statusColor }}>
                  {advice?.action === "perfect"
                    ? "¡Afinado!"
                    : advice?.action === "tighten"
                      ? `+${Math.round(advice.cents)}¢`
                      : `${Math.round(advice?.cents || 0)}¢`}
                </p>

                <div
                  className="w-full max-w-[14rem] mt-5 relative h-2 rounded-full overflow-hidden"
                  style={{ background: "rgba(255,255,255,0.12)" }}
                >
                  <div
                    className="absolute top-0 bottom-0 w-2 rounded-full transition-all"
                    style={{
                      left: `${Math.max(0, Math.min(100, 50 + (state.cents / 50) * 50))}%`,
                      transform: "translateX(-50%)",
                      backgroundColor: statusColor,
                      boxShadow: `0 0 16px ${statusColor}`,
                    }}
                  />
                  <div
                    className="absolute top-0 bottom-0 w-px"
                    style={{ left: "50%", background: "rgba(255,255,255,0.4)" }}
                  />
                </div>
                <div className="flex justify-between w-full max-w-[14rem] mt-2">
                  <span className="text-xs font-sans" style={{ color: T.dim }}>
                    ♭ grave
                  </span>
                  <span className="text-xs font-sans" style={{ color: T.dim }}>
                    agudo ♯
                  </span>
                </div>
              </>
            ) : (
              <div className="text-center px-2">
                <div
                  className="w-3.5 h-3.5 rounded-full mx-auto mb-4"
                  style={{
                    backgroundColor: isListening ? T.mint : T.dim,
                    boxShadow: isListening ? `0 0 20px ${T.mint}` : undefined,
                    animation: isListening ? "tunerPulse 1.4s ease-in-out infinite" : undefined,
                  }}
                />
                <p className="text-sm sm:text-base font-sans leading-snug" style={{ color: T.muted }}>
                  {isListening ? "Toca una cuerda al aire…" : "Activa el micrófono para empezar"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tunerPulse {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );

  return createPortal(modal, document.body);
};

export default TunerOverlay;
